import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabase'
import { montant } from './format'
import {
  t, Stagger, PanelHead, PanelTitle, PageHead, PageTitle, PageSub,
  Btn, IconBtn, Input, Textarea, Select, Label, Field, Row, Cluster, Pill,
  StatGrid, Tile, ChipBar, Chip, Loading, Empty, Muted, scaleIn, fadeIn, useUI,
} from './ui'

import { ORDER_STATUS, GROUP_ORDER, echeance, calcLigne, camionsDe, ORDER_TYPES } from './carnetUtils'
import { ecrire } from './erreurs'

const EMPTY_LIGNE = { produit: '', poids_t: '', prix_100kg: '', taxe_100kg: '0', nb_camions: '' }
const EMPTY_ORDER = { client_id: '', lignes: [{ ...EMPTY_LIGNE }], notes_admin: '', date_livraison: '' }

/* ── En-tête de section ── */
const GroupHead = styled.div`
  display: flex; align-items: center; gap: 9px;
  padding: 0 2px 8px;
  margin-bottom: 9px;
  border-bottom: 2px solid ${(p) => p.$accent};
`
const GroupName = styled.h2`
  font-family: ${t.serif}; font-size: 0.9rem; font-weight: 640;
  letter-spacing: -0.024em; color: ${t.text}; margin: 0;
`
const GroupCount = styled.span`
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 620;
  font-variant-numeric: tabular-nums;
  padding: 1px 7px; border-radius: ${t.rXs};
  background: ${(p) => (p.$tone === 'blue' ? t.blueWash : p.$tone === 'green' ? t.greenWash : p.$tone === 'amber' ? t.amberWash : t.surfaceSunk)};
  color: ${(p) => (p.$tone === 'blue' ? t.blue : p.$tone === 'green' ? t.green : p.$tone === 'amber' ? t.amber : t.textSoft)};
`
const GroupTotals = styled.span`
  margin-left: auto;
  font-family: ${t.font}; font-size: 0.73rem; color: ${t.textFaint};
  font-variant-numeric: tabular-nums;
`
/* Délai avant livraison, coloré selon l'urgence */
const Echeance = styled.div`
  display: inline-block; margin-top: 3px;
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 620;
  padding: 1px 7px; border-radius: ${t.rXs};
  background: ${(p) => (p.$tone === 'late' ? t.redWash : p.$tone === 'urgent' ? t.amberWash : p.$tone === 'soon' ? t.blueWash : t.surfaceSunk)};
  color: ${(p) => (p.$tone === 'late' ? t.red : p.$tone === 'urgent' ? t.amber : p.$tone === 'soon' ? t.blue : t.textFaint)};
`

const Cell = styled.div`min-width: 0;`
/* Repère discret mais visible : il signale ce qui manque encore sur une
   commande — un montant compta, un coût de revient. Deux teintes pour
   ne pas les confondre d'un coup d'œil. */
const Manque = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 3px; padding: 1px 7px; border-radius: ${t.rXs};
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 600;
  background: ${(p) => (p.$tone === 'teal' ? t.tealWash : t.amberWash)};
  color: ${(p) => (p.$tone === 'teal' ? t.teal : t.amber)};
  border: 1px solid ${(p) => (p.$tone === 'teal' ? '#CFE5E6' : '#F0DFC2')};
  &::before { content: '●'; font-size: 0.5rem; }
`
const CellLabel = styled.div`
  font-size: 0.66rem; color: ${t.textFaint}; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 3px;
`
const CellMain = styled.div`
  font-size: 0.85rem; font-weight: 700; color: ${t.text};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
const CellSub = styled.div`font-size: 0.72rem; color: ${t.textSoft}; margin-top: 2px;`
const GoLink = styled.span`
  font-size: 0.76rem; font-weight: 700; color: ${t.textSoft}; white-space: nowrap;
  @media (max-width: 1100px) { display: none; }
`

/* ── Styles ── */
const OrderCard = styled.button`
  position: relative;
  width: 100%; text-align: left;
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-left: 2px solid ${(p) => p.$accent};
  border-radius: ${t.r};
  padding: 12px 16px;
  margin-bottom: 7px;
  cursor: pointer;
  font-family: ${t.font};
  display: grid;
  grid-template-columns: 1.6fr 1.4fr 0.9fr 1fr 1.1fr auto;
  gap: 14px;
  align-items: center;
  transition: background 0.14s, border-color 0.14s;

  /* Une commande livrée est un dossier clos : on la met en retrait
     pour que les livraisons à venir ressortent seules. */
  ${(p) => p.$done && css`
    background: ${t.surfaceAlt};
    border-left-style: dashed;
    opacity: 0.72;
  `}

  &:hover {
    background: ${t.surfaceAlt};
    border-color: #DDDFDA;
    border-left-color: ${(p) => p.$accent};
    opacity: 1;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
    row-gap: 12px;
  }

  /* Mobile : trois lignes compactes au lieu de cinq blocs empilés.
     ligne 1  client · statut
     ligne 2  marchandise
     ligne 3  volume · CA                                        */
  @media (max-width: 620px) {
    grid-template-columns: 1fr auto;
    gap: 4px 10px;
    padding: 10px 12px;
    margin-bottom: 6px;

    ${CellLabel} { display: none; }

    /* Intitulé et valeur côte à côte plutôt que l'un sous l'autre */
    ${CellSub} { margin-top: 0; }

    & > *:nth-child(1) { grid-area: 1 / 1 / 2 / 2; }
    & > *:nth-child(5) { grid-area: 1 / 2 / 2 / 3; text-align: right; }
    & > *:nth-child(2) { grid-area: 2 / 1 / 3 / 3; }
    & > *:nth-child(3) {
      grid-area: 3 / 1 / 4 / 2;
      display: flex; align-items: baseline; gap: 6px;
    }
    & > *:nth-child(4) {
      grid-area: 3 / 2 / 4 / 3;
      display: flex; align-items: baseline; gap: 6px; justify-content: flex-end;
    }
  }
`

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 3000;
  background: rgba(22,24,28,0.32);
  display: flex; align-items: center; justify-content: center; padding: 18px;
  animation: ${fadeIn} 0.16s ease both;
`
const Sheet = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: 12px;
  padding: 24px;
  width: 100%; max-width: ${(p) => p.$w || '620px'};
  max-height: 90vh; overflow-y: auto;
  box-shadow: 0 12px 40px rgba(22,24,28,0.16);
  animation: ${scaleIn} 0.2s cubic-bezier(.22,.9,.3,1) both;
`
const LigneBox = styled.div`
  background: ${t.surfaceAlt};
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  padding: 12px 14px;
  margin-bottom: 8px;
`
const TotalBanner = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  background: ${(p) => (p.$blue ? t.blueWash : t.goldWash)};
  border: 1px solid ${(p) => (p.$blue ? '#D3E4F3' : '#CFE2DC')};
  border-radius: ${t.rSm};
  padding: 11px 15px;
  font-family: ${t.font};
  margin-bottom: 10px;
  span:first-child {
    font-size: 0.625rem; font-weight: 600; letter-spacing: 0.075em;
    text-transform: uppercase; color: ${(p) => (p.$blue ? t.blue : t.gold)}; opacity: 0.75;
  }
  span:last-child {
    font-family: ${t.serif}; font-size: 0.95rem; font-weight: 640; letter-spacing: -0.026em;
    font-variant-numeric: tabular-nums;
    color: ${(p) => (p.$blue ? t.blue : t.gold)};
  }
`
const TypeCard = styled.button`
  width: 100%; text-align: left;
  padding: 14px 16px; border-radius: ${t.r}; cursor: pointer;
  font-family: ${t.font};
  background: ${t.surface};
  border: 1px solid ${t.line};
  transition: background 0.14s, border-color 0.14s;
  &:hover {
    border-color: ${(p) => (p.$blue ? t.blue : t.gold)};
    background: ${(p) => (p.$blue ? t.blueWash : t.goldWash)};
  }
  div:first-child {
    font-weight: 600; font-size: 0.865rem; letter-spacing: -0.018em;
    color: ${(p) => (p.$blue ? t.blue : t.gold)}; margin-bottom: 3px;
  }
  div:last-child { font-size: 0.75rem; color: ${t.textSoft}; line-height: 1.45; }
`
const DialogTitle = styled.h3`
  font-family: ${t.serif}; font-size: 1.04rem; font-weight: 650;
  letter-spacing: -0.028em; color: ${t.text}; margin: 0 0 6px;
`

export default function CarnetAdmin({ onValidationAlert }) {
  const navigate = useNavigate()
  const { toast } = useUI()
  const [orders, setOrders] = useState([])
  const [clients, setClients] = useState([])
  /* Un coût peut avoir été figé sur le devis avant que les commandes ne
     portent le leur : sans ce relevé, on signalerait un manque à tort. */
  const [devisChiffres, setDevisChiffres] = useState(new Set())
  /* Commandes découpées en plusieurs camions : la date affichée n'est
     alors que celle de la première tournée, il faut le dire. */
  const [nbTournees, setNbTournees] = useState({})
  const [produits, setProduits] = useState([])
  const [loading, setLoading] = useState(true)
  const [typeModal, setTypeModal] = useState(null)
  const [view, setView] = useState('validated')
  const [creating, setCreating] = useState(false)
  const [newOrder, setNewOrder] = useState(EMPTY_ORDER)

  useEffect(() => {
    fetchOrders()
    supabase.from('clients').select('id, name, email').order('name').then(({ data }) => setClients(data || []))
    supabase.from('products').select('id, name').eq('available', true).order('name').then(({ data }) => setProduits(data || []))
    const channel = supabase.channel('orders-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orders' }, (payload) => {
        if (payload.new.status === 'validated' && payload.old.status !== 'validated') onValidationAlert()
        setOrders((prev) => prev.map((o) => (o.id === payload.new.id ? { ...o, ...payload.new } : o)))
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrders = async () => {
    setLoading(true)
    const { data } = await supabase.from('orders').select('*, clients(name, email, phone, canton)').order('created_at', { ascending: false })
    const { data: tr } = await supabase.from('tournees').select('order_id')
    const compte = {}
    ;(tr || []).forEach((t) => { compte[t.order_id] = (compte[t.order_id] || 0) + 1 })
    setNbTournees(compte)

    const quoteIds = (data || []).filter((o) => !o.cout_revient && o.quote_id).map((o) => o.quote_id)
    if (quoteIds.length) {
      const { data: q } = await supabase.from('quotes')
        .select('id').in('id', quoteIds).not('cout_revient', 'is', null)
      setDevisChiffres(new Set((q || []).map((x) => x.id)))
    }
    setOrders(data || [])
    setLoading(false)
  }

  const updateLigneNew = (i, field, val) =>
    setNewOrder((p) => ({ ...p, lignes: p.lignes.map((l, idx) => (idx === i ? { ...l, [field]: val } : l)) }))

  const createOrder = () => {
    if (!newOrder.client_id) return toast('Sélectionnez un client', 'error')
    if (newOrder.lignes.some((l) => !l.produit || !l.prix_100kg || (l.produit !== 'Frais de douane' && !l.poids_t))) {
      return toast('Remplissez tous les champs obligatoires', 'error')
    }
    const lignesData = newOrder.lignes.map((l) => {
      const c = calcLigne(l)
      return { produit: l.produit, poids_t: c.p, prix_100kg: c.pr, taxe_100kg: c.tx, nb_camions: c.nb, sous_total: c.total }
    })
    setTypeModal({
      lignesData,
      poidsTotal: lignesData.reduce((acc, l) => acc + l.poids_t, 0),
      caTotal: lignesData.reduce((acc, l) => acc + l.sous_total, 0),
      caReel: '',
      client_id: newOrder.client_id,
      notes_admin: newOrder.notes_admin,
      date_livraison: newOrder.date_livraison,
    })
  }

  const confirmCreateOrder = async (type, caReel) => {
    const { lignesData, poidsTotal, caTotal, client_id, notes_admin, date_livraison } = typeModal
    const isFacture = type === 'facture'

    /* Une pro forma attend sa livraison : elle entre dans le carnet au
       même titre qu'une commande validée, et se clôture plus tard. */
    const status = isFacture ? 'delivered'
      : type === 'proforma' ? (date_livraison ? 'scheduled' : 'validated')
      : 'sent'

    const ok = await ecrire(
      supabase.from('orders').insert([{
        client_id, lignes: lignesData,
        poids_total_t: poidsTotal, ca_total_chf: caTotal,
        ca_reel_chf: isFacture && caReel ? parseFloat(caReel) : (isFacture ? caTotal : null),
        status,
        type,
        notes_admin,
        date_livraison: date_livraison || null,
      }]),
      toast, 'création de la commande',
    )
    if (!ok) return

    // Facture comme pro forma engagent le client : il passe en fidèle
    if (type !== 'devis' && client_id) {
      await supabase.from('clients').update({ type: 'fidele' }).eq('id', client_id)
    }
    toast(isFacture ? 'Facture enregistrée' : type === 'proforma' ? 'Pro forma créée' : 'Devis créé')
    setCreating(false)
    setTypeModal(null)
    setNewOrder(EMPTY_ORDER)
    fetchOrders()
  }

  const filtered =
    view === 'validated' ? orders.filter((o) => ['validated', 'scheduled', 'delivered'].includes(o.status))
    : ORDER_STATUS[view] ? orders.filter((o) => o.status === view)
    : orders

  /* Regroupement par statut : c'est ce qui sépare le plus nettement
     les livraisons à venir de celles déjà faites. */
  const groupes = GROUP_ORDER
    .map((key) => {
      let lignes = filtered.filter((o) => o.status === key)
      // Les livraisons planifiées se lisent par date, la plus proche d'abord
      if (key === 'scheduled') {
        lignes = [...lignes].sort((a, b) =>
          new Date(a.date_livraison || '2999-01-01') - new Date(b.date_livraison || '2999-01-01'))
      }
      // Les livraisons faites se lisent de la plus récente à la plus ancienne
      if (key === 'delivered') {
        lignes = [...lignes].sort((a, b) =>
          new Date(b.date_livraison || b.created_at) - new Date(a.date_livraison || a.created_at))
      }
      return { key, ...ORDER_STATUS[key], lignes }
    })
    .filter((g) => g.lignes.length > 0)

  const totalPoids = filtered.reduce((acc, o) => acc + (o.poids_total_t || 0), 0)
  const totalCA = filtered.reduce((acc, o) => acc + (o.ca_total_chf || 0), 0)
  const totalCaReel = filtered.reduce((acc, o) => acc + (o.ca_reel_chf || 0), 0)
  const totalCompta = filtered.reduce((acc, o) => acc + (o.ca_compta_chf || 0), 0)
  const totalCamions = filtered.reduce((acc, o) => acc + camionsDe(o), 0)

  const newTotal = newOrder.lignes.reduce((acc, l) => acc + calcLigne(l).total, 0)
  const newPoids = newOrder.lignes.reduce((acc, l) => acc + (parseFloat(l.poids_t) || 0), 0)
  const newCamions = newOrder.lignes.reduce((acc, l) => acc + calcLigne(l).nb, 0)

  return (
    <>
      <PageHead>
        <div>
          <PageTitle>Carnet de commandes</PageTitle>
          <PageSub>{orders.length} commande(s) enregistrée(s)</PageSub>
        </div>
        <Btn $variant="gold" onClick={() => setCreating(true)}>+ Nouvelle commande</Btn>
      </PageHead>

      <ChipBar $scroll style={{ marginBottom: 18 }}>
        <Chip $on={view === 'validated'} onClick={() => setView('validated')}>
          ✅ Validées / en cours <span>{orders.filter((o) => ['validated', 'scheduled', 'delivered'].includes(o.status)).length}</span>
        </Chip>
        <Chip $on={view === 'all'} onClick={() => setView('all')}>
          📋 Toutes <span>{orders.length}</span>
        </Chip>
        {GROUP_ORDER.map((key) => {
          const n = orders.filter((o) => o.status === key).length
          if (!n) return null
          return (
            <Chip key={key} $on={view === key} onClick={() => setView(key)}>
              {ORDER_STATUS[key].label.split(' ')[0]} {ORDER_STATUS[key].court} <span>{n}</span>
            </Chip>
          )
        })}
      </ChipBar>

      <StatGrid style={{ marginBottom: 20 }}>
        <Tile i={0} label="Poids total" value={`${totalPoids.toFixed(1)} T`} hint={`${totalCamions} camion(s)`} tone="neutral" />
        <Tile i={1} label="Engagé (devis)" value={montant(totalCA)} hint="montant calculé" tone="gold" />
        <Tile i={2} label="Compta" value={totalCompta > 0 ? montant(totalCompta) : '—'} hint="facturé et déclaré" tone="blue" />
        <Tile i={3} label="Réel perçu" value={totalCaReel > 0 ? montant(totalCaReel) : '—'}
          hint={totalCompta > 0 && totalCaReel > 0 ? `${montant(totalCaReel - totalCompta, { signe: true })} vs compta` : 'ce qui a été gagné'}
          tone="teal" />
      </StatGrid>

      {/* ══ Liste ══ */}
      {loading ? (
        <Loading rows={4} h="82px" />
      ) : groupes.length === 0 ? (
        <Empty icon="🚛" title="Aucune commande">
          {view === 'validated' ? 'Aucune commande validée pour le moment.' : 'Créez une commande manuelle ou envoyez un devis depuis l’onglet Devis.'}
        </Empty>
      ) : (
        groupes.map((g) => {
          const poids = g.lignes.reduce((a, o) => a + (o.poids_total_t || 0), 0)
          const ca = g.lignes.reduce((a, o) => a + (o.ca_total_chf || 0), 0)
          /* Livraisons faites dont le montant compta manque : elles ne
             comptent dans aucune statistique tant qu'il n'est pas saisi. */
          const aValider = g.key === 'delivered'
            ? g.lignes.filter((o) => o.ca_compta_chf == null).length
            : 0
          return (
            <section key={g.key} style={{ marginBottom: 22 }}>
              <GroupHead $accent={g.accent}>
                <GroupName>{g.label.replace(/^\S+\s/, '')}</GroupName>
                <GroupCount $tone={g.tone}>{g.lignes.length}</GroupCount>
                <GroupTotals>
                  {poids.toFixed(1)} T · {montant(ca)}
                  {aValider > 0 && (
                    <b style={{ color: t.amber, fontWeight: 600 }}>
                      {' · '}{aValider} à valider en compta
                    </b>
                  )}
                </GroupTotals>
              </GroupHead>

              {g.lignes.map((o, i) => {
                const marchandise = (o.lignes || []).map((l) => l.produit).join(', ')
                const nbCamions = camionsDe(o)
                const ech = g.key === 'scheduled' ? echeance(o.date_livraison) : null
                const livree = g.key === 'delivered'
                return (
                  <Stagger key={o.id} $i={i}>
                    <OrderCard
                      $accent={g.accent}
                      $done={livree}
                      onClick={() => navigate(`/admin/carnet/${o.id}`)}
                    >
                      <Cell>
                        <Cluster $gap="7px">
                          <CellMain>{o.clients?.name || '—'}</CellMain>
                          {ORDER_TYPES[o.type] && o.type !== 'devis' && (
                            <Pill $tone={ORDER_TYPES[o.type].tone}>{ORDER_TYPES[o.type].label}</Pill>
                          )}
                        </Cluster>
                        <CellSub style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {o.clients?.email}{o.clients?.canton ? ` · ${o.clients.canton}` : ''}
                        </CellSub>
                      </Cell>

                      <Cell>
                        <CellLabel>Marchandise</CellLabel>
                        <CellMain style={{ fontWeight: 500 }}>{marchandise || '—'}</CellMain>
                      </Cell>

                      <Cell>
                        <CellLabel>Volume</CellLabel>
                        <CellMain>{o.poids_total_t || 0} T</CellMain>
                        <CellSub>🚛 {nbCamions} camion(s)</CellSub>
                      </Cell>

                      <Cell>
                        <CellLabel>CA</CellLabel>
                        <CellMain style={{ color: livree ? t.textSoft : t.green }}>
                          {montant(o.ca_total_chf)}
                        </CellMain>
                        {o.ca_reel_chf ? <CellSub style={{ color: t.blue, fontWeight: 600 }}>Réel : {montant(o.ca_reel_chf)}</CellSub> : null}
                        {/* Le total compta ne retient que les livraisons dont le
                            montant est saisi. Sans repère ici, une livraison
                            oubliée manquait aux statistiques sans qu'on le voie. */}
                        {o.ca_compta_chf != null ? (
                          <CellSub style={{ color: t.green, fontWeight: 600 }}>
                            Compta : {montant(o.ca_compta_chf)}
                          </CellSub>
                        ) : livree ? (
                          <Manque>À valider en compta</Manque>
                        ) : null}
                        {/* Sans coût figé, la commande sort des marges et du
                            prévisionnel : le signaler ici évite de s'en
                            apercevoir en lisant des totaux incomplets. */}
                        {g.key !== 'sent' && !o.cout_revient && !devisChiffres.has(o.quote_id) && (
                          <Manque $tone="teal">Coût de revient à calculer</Manque>
                        )}
                      </Cell>

                      <Cell>
                        <CellLabel>
                          {nbTournees[o.id] > 1
                            ? `${nbTournees[o.id]} tournées · 1ʳᵉ`
                            : livree ? 'Livrée le' : 'Livraison'}
                        </CellLabel>
                        {o.date_livraison ? (
                          <>
                            <CellMain style={{ fontWeight: 600 }}>
                              {new Date(o.date_livraison).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </CellMain>
                            {ech && <Echeance $tone={ech.tone}>{ech.texte}</Echeance>}
                          </>
                        ) : (
                          <CellMain style={{ fontWeight: 500, color: t.textFaint }}>
                            {livree ? '—' : 'à planifier'}
                          </CellMain>
                        )}
                      </Cell>

                      <GoLink>Gérer →</GoLink>
                    </OrderCard>
                  </Stagger>
                )
              })}
            </section>
          )
        })
      )}

      {/* ══ Création ══ */}
      {creating && (
        <Overlay onMouseDown={(e) => e.target === e.currentTarget && setCreating(false)}>
          <Sheet $w="720px">
            <PanelHead>
              <PanelTitle>Nouvelle commande manuelle</PanelTitle>
              <IconBtn $sm onClick={() => setCreating(false)}>✕</IconBtn>
            </PanelHead>

            <Field>
              <Label>Client *</Label>
              <Select value={newOrder.client_id} onChange={(e) => setNewOrder((p) => ({ ...p, client_id: e.target.value }))}>
                <option value="">— Sélectionner un client —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </Select>
            </Field>

            <Label>Lignes de produits *</Label>
            {newOrder.lignes.map((l, i) => {
              const c = calcLigne(l)
              const douane = l.produit === 'Frais de douane'
              return (
                <LigneBox key={i}>
                  <Cluster style={{ justifyContent: 'space-between', marginBottom: 10 }}>
                    <Label style={{ margin: 0 }}>Ligne {i + 1}</Label>
                    {newOrder.lignes.length > 1 && (
                      <IconBtn $sm onClick={() => setNewOrder((p) => ({ ...p, lignes: p.lignes.filter((_, idx) => idx !== i) }))}>✕</IconBtn>
                    )}
                  </Cluster>
                  <Row $cols={douane ? '2fr 1fr' : '2fr 1fr 1fr 1fr 1fr'} $gap="10px">
                    <Field $flush>
                      <Label>Produit *</Label>
                      <Select value={l.produit} onChange={(e) => updateLigneNew(i, 'produit', e.target.value)}>
                        <option value="">— Choisir —</option>
                        {produits.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                        <option value="Frais de douane">🛃 Frais de douane</option>
                      </Select>
                    </Field>
                    {douane ? (
                      <Field $flush>
                        <Label>Montant *</Label>
                        <Input type="number" value={l.prix_100kg}
                          onChange={(e) => { updateLigneNew(i, 'prix_100kg', e.target.value); updateLigneNew(i, 'poids_t', '1'); updateLigneNew(i, 'taxe_100kg', '0') }}
                          placeholder="82" />
                      </Field>
                    ) : (
                      <>
                        <Field $flush><Label>Poids (T) *</Label><Input type="number" value={l.poids_t} onChange={(e) => updateLigneNew(i, 'poids_t', e.target.value)} placeholder="23" /></Field>
                        <Field $flush><Label>Prix /100kg *</Label><Input type="number" value={l.prix_100kg} onChange={(e) => updateLigneNew(i, 'prix_100kg', e.target.value)} placeholder="18.50" /></Field>
                        <Field $flush><Label>Taxe /100kg</Label><Input type="number" value={l.taxe_100kg} onChange={(e) => updateLigneNew(i, 'taxe_100kg', e.target.value)} placeholder="0" /></Field>
                        <Field $flush><Label>Nb camions</Label><Input type="number" value={l.nb_camions} onChange={(e) => updateLigneNew(i, 'nb_camions', e.target.value)} placeholder="auto" /></Field>
                      </>
                    )}
                  </Row>
                  {l.poids_t && l.prix_100kg && (
                    <div style={{ textAlign: 'right', marginTop: 8, fontFamily: t.font, fontSize: '0.8rem', fontWeight: 700, color: t.green }}>
                      Sous-total : {montant(c.total)}
                    </div>
                  )}
                </LigneBox>
              )
            })}

            <Btn $variant="ghost" $size="sm" style={{ marginBottom: 16 }}
              onClick={() => setNewOrder((p) => ({ ...p, lignes: [...p.lignes, { ...EMPTY_LIGNE }] }))}>
              + Ajouter une ligne
            </Btn>

            <Row $cols="1fr 1fr">
              <Field><Label>Date de livraison prévue</Label><Input type="date" value={newOrder.date_livraison} onChange={(e) => setNewOrder((p) => ({ ...p, date_livraison: e.target.value }))} /></Field>
            </Row>
            <Field>
              <Label>Notes internes</Label>
              <Textarea value={newOrder.notes_admin} onChange={(e) => setNewOrder((p) => ({ ...p, notes_admin: e.target.value }))} placeholder="Infos logistiques, remarques…" />
            </Field>

            {newOrder.lignes.some((l) => l.poids_t && l.prix_100kg) && (
              <TotalBanner>
                <span>Total</span>
                <span>{montant(newTotal)} · {newPoids} T · {newCamions} camions</span>
              </TotalBanner>
            )}

            <Btn $variant="gold" $block onClick={createOrder}>✓ Créer la commande</Btn>
          </Sheet>
        </Overlay>
      )}
      {/* ══ Modale type devis / facture ══ */}
      {typeModal && (
        <Overlay onMouseDown={(e) => e.target === e.currentTarget && setTypeModal(null)}>
          <Sheet $w="450px">
            <DialogTitle>Quel type de commande ?</DialogTitle>
            <Muted style={{ marginBottom: 22 }}>
              Montant : <strong style={{ color: t.text }}>{montant(typeModal.caTotal)} · {typeModal.poidsTotal.toFixed(1)} T</strong>
            </Muted>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 14 }}>
              <TypeCard onClick={() => confirmCreateOrder('devis', null)}>
                <div>📋 Devis</div>
                <div>Une offre, rien n'est engagé. Statut « Devis envoyé ».</div>
              </TypeCard>

              <TypeCard onClick={() => confirmCreateOrder('proforma', null)}>
                <div>📄 Pro forma</div>
                <div>
                  Commande ferme en attente de livraison. Entre dans le carnet et se
                  clôture plus tard, avec les quantités et la TVA réelles.
                </div>
              </TypeCard>

              {!typeModal.showCaReel ? (
                <TypeCard $blue onClick={() => setTypeModal((p) => ({ ...p, showCaReel: true }))}>
                  <div>🧾 Facture</div>
                  <div>Statut « Livré » — hors stats devis — client passé en fidèle</div>
                </TypeCard>
              ) : (
                <div style={{ border: `2px solid ${t.blue}`, borderRadius: 13, padding: '16px 19px' }}>
                  <div style={{ fontFamily: t.font, fontWeight: 700, color: t.blue, marginBottom: 12 }}>🧾 Facture — CA réel</div>
                  <Field>
                    <Label>CA réel encaissé</Label>
                    <Input type="number" value={typeModal.caReel} autoFocus
                      onChange={(e) => setTypeModal((p) => ({ ...p, caReel: e.target.value }))}
                      placeholder={typeModal.caTotal.toFixed(0)} />
                    <Muted style={{ fontSize: '0.75rem', marginTop: 6 }}>Laisser vide pour utiliser le CA prévisionnel.</Muted>
                  </Field>
                  <Btn $variant="gold" $block onClick={() => confirmCreateOrder('facture', typeModal.caReel)}>✓ Confirmer la facture</Btn>
                </div>
              )}
            </div>

            <Btn $variant="ghost" $block onClick={() => setTypeModal(null)}>Annuler</Btn>
          </Sheet>
        </Overlay>
      )}

    </>
  )
}
